import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { pxToRem } from '../../../utils/pxToRem';
import { conventionContent } from '../content';
import { SectionInner } from '../components/SectionInner';
import { CornerFrame } from '../components/CornerFrame';

export function ExpressionOfInterest() {
  const { expressionOfInterest } = conventionContent;
  return (
    <Box
      component='section'
      id={expressionOfInterest.sectionId}
      aria-labelledby={expressionOfInterest.headingId}
      sx={{
        backgroundColor: 'background.paper',
        paddingBlock: { mobile: pxToRem(48), laptop: pxToRem(120) },
      }}
    >
      <SectionInner
        direction={{ mobile: 'column', laptop: 'row' }}
        gap={{ mobile: pxToRem(48), laptop: pxToRem(96) }}
        sx={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { mobile: pxToRem(300), laptop: pxToRem(288) },
            height: { mobile: pxToRem(309), laptop: pxToRem(392) },
            flexShrink: 0,
          }}
        >
          <CornerFrame
            corner='bottom-right'
            inset={{
              mobile: { horizontal: 17, vertical: 21 },
              laptop: { horizontal: 17, vertical: 24 },
            }}
            radius={{ mobile: 25, laptop: 29 }}
          />
          <Stack
            gap={pxToRem(24)}
            sx={{
              position: 'absolute',
              top: { mobile: pxToRem(6), laptop: pxToRem(6) },
              left: { mobile: '50%', laptop: pxToRem(127.5) },
              transform: 'translateX(-50%)',
              width: { mobile: pxToRem(261), laptop: pxToRem(255) },
              alignItems: 'center',
              textAlign: { mobile: 'center', laptop: 'start' },
              color: 'primary.main',
            }}
          >
            <Typography
              component='h2'
              id={expressionOfInterest.headingId}
              variant='h2'
              sx={{
                fontSize: { mobile: pxToRem(24), laptop: pxToRem(32) },
                lineHeight: { mobile: pxToRem(32), laptop: pxToRem(40) },
                margin: 0,
                width: '100%',
              }}
            >
              {expressionOfInterest.heading}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Roboto", sans-serif',
                fontWeight: 700,
                fontSize: { mobile: pxToRem(16), laptop: pxToRem(18) },
                lineHeight: pxToRem(20),
                margin: 0,
              }}
            >
              {expressionOfInterest.lead}
            </Typography>
            <Stack gap={pxToRem(8)} sx={{ alignItems: { mobile: 'center', laptop: 'start' }, width: '100%' }}>
              <Stack gap={0} sx={{ color: 'secondary.main' }}>
                {expressionOfInterest.invitation.map((line, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 700,
                      fontSize: { mobile: pxToRem(16), laptop: pxToRem(18) },
                      lineHeight: pxToRem(20),
                      margin: 0,
                    }}
                    color='secondary.main'
                  >
                    {line}
                  </Typography>
                ))}
              </Stack>
              <Link
                href={`mailto:${expressionOfInterest.email}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingInline: pxToRem(8),
                  // Figma strokes sit inside the 28px chip
                  paddingBlock: { mobile: pxToRem(4), laptop: pxToRem(3) },
                  width: pxToRem(233),
                  border: '1px solid',
                  borderColor: 'secondary.main',
                  borderRadius: pxToRem(4),
                  color: 'secondary.main',
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 400,
                  fontSize: pxToRem(16),
                  lineHeight: pxToRem(20),
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {expressionOfInterest.email}
              </Link>
            </Stack>
          </Stack>
        </Box>

        <Stack
          direction={{ mobile: 'column', laptop: 'row' }}
          gap={{ mobile: pxToRem(60), laptop: pxToRem(128) }}
          sx={{
            width: { mobile: pxToRem(298), laptop: 'auto' },
            alignItems: { mobile: 'center', laptop: 'flex-start' },
          }}
        >
          {expressionOfInterest.cards.map((card, index) => (
            <Stack
              key={index}
              gap={pxToRem(24)}
              sx={{
                width: { mobile: '100%', laptop: pxToRem(161) },
                alignItems: { mobile: 'center', laptop: 'flex-start' },
                textAlign: { mobile: 'center', laptop: 'left' },
                color: 'primary.main',
              }}
            >
              <Box
                component='img'
                src={card.iconSrc}
                alt={card.iconAlt}
                aria-hidden='true'
                sx={{
                  width: pxToRem(160),
                  height: pxToRem(160),
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
              <Stack gap={0}>
                <Typography
                  component='p'
                  sx={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    fontSize: { mobile: pxToRem(14), laptop: pxToRem(16) },
                    lineHeight: pxToRem(20),
                    margin: 0,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  component='p'
                  sx={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 400,
                    fontSize: { mobile: pxToRem(14), laptop: pxToRem(16) },
                    lineHeight: pxToRem(20),
                    margin: 0,
                  }}
                >
                  {card.body}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </SectionInner>
    </Box>
  );
}
